/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Client, ClientUser} from './client';
import {User} from './user';
import {History} from './history';
import {SearchableItem} from './interface/searchable.item';
import {SearchableSelectionType} from './interface/searchable.selection';
import {State} from './state';
import {CheckList} from './checklist';
import {CorporateEntity} from './corporate.entity';
import {Stage} from './stage';
import {HistoryStage} from './history.stage';
import {ReportNote} from './report.note';
import * as moment from 'moment';
import {Tag} from './tag';
import {IReport} from './interface/ireport';
import {SystemType} from './enums/system.type';
import {Quote} from './quote';
import {ReportFollowUp} from './report.followup';
import {ReportLink} from './report-link';
import {LinkAccess} from './link-access';
import {BVQReport} from './bvq/bvq.report';
import { ModuleType } from './enums/module.type';
import {Document} from './document';

export class Report implements IReport, SearchableItem {
  ID: string;
  ENTITIES: CorporateEntity[];
  IS_FRANCHISE: boolean;
  IMPORTED: boolean;
  FRANCHISE: string;
  IS_FIRST_FOLLOWUP_COMPLETED:boolean;
  IS_SECOND_FOLLOWUP_COMPLETED:boolean;
  IS_MULTIPLE_ENTITY: boolean;
  IS_MULTIPLE_FINANCIALS : boolean;
  DATE_CREATED: Date;
  DATE_UPDATED: Date;
  DATE_ENGAGED: string;
  DATE_ENGAGED_LETTER: string;
  dateSiteVisitEmail: string;
  DATE_LATEST_FOLLOW_UP: Date;
  DATE_QUOTED: Date;
  DATE_DUE: string;
  DATE_DUE_TO: string;
  DATE_DUE_DIFF: number;
  DATE_DUE_EXPIRED: boolean;
  TURN_FROM: number;
  TURN_TO: number;
  CLIENT: Client;
  CLIENT_USERS: ReportClientUser[];
  USERS: ReportUsers;
  STANDARD_OF_VALUE: StandardOfValue;
  CHARGE: number;
  CHARGE_DESC: string;
  PURPOSE: string;
  VALUATION_TYPE: ReportValuationType;
  UPLOAD_PATH: string;
  UPLOAD_DATE: Date;

  STAGE: Stage;
  STATE: State;

  ASSIGNEE: User;

  HISTORY: History[];

  WATCHERS: User[];
  TAGS: Tag[];
  IS_WATCHING: boolean;

  DELAYS: ReportDelay[];
  ACTIVE_DELAY: ReportDelay;

  LINKS: Link[];
  REPORT_NOTES: ReportNote[];

  CHECK_LIST: CheckList;

  //List of Document
  DOCUMENTS: Document[];
  DOCUMENT_ID: string;
  DOCUMENT_STATE: DocumentState;

  NOTES: string;
  COMMENTS: string;
  OVERRIDE: boolean;
  OVERRIDE_NAME: string;
  ADDENDUM: boolean;
  ADDENDUM_CONTENT: string;

  DATE_ANTICIPATED: string;
  REPORT_TYPE: ReportType;
  INITIAL_SENT: boolean;
  INTERNAL_FILE_NAME: string;
  FILE_LOCATION: string;
  PARENT_ID: string;
  DUPLICATE_PARENT_ID: string;

  TYPE: SystemType;
  DATE_EFFECTIVE: string;
  STANDARD_OF_VALUES: string[];
  REPORT_QUOTE: Quote;
  ORDER: boolean;
  IS_QUOTE: boolean;
  FOLLOW_UPS: ReportFollowUp[];
  BVQ_ID: string;
  LINKED_REPORTS: ReportLink[];
  SYNCHRONIZE: boolean;
  ORDER_ID: string;
  R_RESEARCH: User;
  R_SPREAD: User;
  NOTES_COUNT: number;
  LINK_ACCESS: LinkAccess[];
  RATING: number;
  ENG_LETTER_CONTENT: string;
  ENG_LETTER_CONTENT_DS: string;
  DAY_DIFF: number;
  FIRST_QUOTE_FOLLOW_UP: number;
  SECOND_QUOTE_FOLLOW_UP: number;
  FIRST_QUOTE_FOLLOW_UP_TIME: string;
  SECOND_QUOTE_FOLLOW_UP_TIME: string;
  INDIVIDUAL_CAN_ANSWER : string;

  LL_NUMBER: string;

  // Modules
  HAS_BVQ: boolean;
  BVQ: BVQReport;
  PROJECT_NUMBER: string;

  ERROR : string;
  VALUE_ASSESSMENT: string;

  // Helpers
  selected = false;
  ignore_collector = false;
  isLoading = false;
  active: boolean;
  allowed: boolean;
  show_bvq_button: boolean = false;



  static parse(json: any): Report {
    if (json) {
      const r = new Report();
      r.ID = json.ID;
      r.ENTITIES = CorporateEntity.parseArray(json.ENTITIES);
      r.ERROR = json.ERROR
      if (json.IS_MULTIPLE_ENTITY)
        r.IS_MULTIPLE_ENTITY = json.IS_MULTIPLE_ENTITY;
      else r.IS_MULTIPLE_ENTITY = false;

      if (json.IS_MULTIPLE_FINANCIALS)
        r.IS_MULTIPLE_FINANCIALS = json.IS_MULTIPLE_FINANCIALS;
      else r.IS_MULTIPLE_FINANCIALS = false;

      if (json.IS_FIRST_FOLLOWUP_COMPLETED)
        r.IS_FIRST_FOLLOWUP_COMPLETED = json.IS_FIRST_FOLLOWUP_COMPLETED;
      else r.IS_FIRST_FOLLOWUP_COMPLETED = false;

      if (json.IS_SECOND_FOLLOWUP_COMPLETED)
        r.IS_SECOND_FOLLOWUP_COMPLETED = json.IS_SECOND_FOLLOWUP_COMPLETED;
      else r.IS_SECOND_FOLLOWUP_COMPLETED = false;

      r.IS_FRANCHISE = json.IS_FRANCHISE;
      r.FRANCHISE = json.FRANCHISE;
      r.IMPORTED = Boolean(json.IMPORTED);
      r.DATE_CREATED = json.DATE_CREATED;
      r.DATE_UPDATED = json.DATE_UPDATED;
      r.DATE_LATEST_FOLLOW_UP = json.DATE_LATEST_FOLLOW_UP;
      r.DATE_QUOTED = json.DATE_QUOTED;

      if (json.DATE_ENGAGED)
        r.DATE_ENGAGED = moment(json.DATE_ENGAGED).format('MMM DD, YYYY');

      if (json.DATE_ENGAGED_LETTER)
        r.DATE_ENGAGED_LETTER = moment(json.DATE_ENGAGED_LETTER).format('MMM DD, YYYY');

      r.DATE_DUE = json.DATE_DUE;
      r.DATE_DUE_TO = json.DATE_DUE_TO;
      r.DATE_DUE_DIFF = json.DATE_DUE_DIFF;
      r.DATE_DUE_EXPIRED = json.DATE_DUE_EXPIRED;

      r.TURN_FROM = json.TURN_FROM;
      r.TURN_TO = json.TURN_TO;
      r.CLIENT = Client.parse(json.CLIENT);
      r.CLIENT_USERS = ReportClientUser.parseArray(json.CLIENT_USERS);
      r.USERS = ReportUsers.parse(json.USERS);
      r.STANDARD_OF_VALUE = StandardOfValue[<string>json.STANDARD_OF_VALUE];
      r.CHARGE = Number(json.CHARGE);
      r.CHARGE_DESC = json.CHARGE_DESC;
      r.PURPOSE = json.PURPOSE;
      r.VALUATION_TYPE = ReportValuationType[<string>json.VALUATION_TYPE];
      r.TAGS = Tag.parseArray(json.TAGS);

      r.UPLOAD_PATH = json.UPLOAD_PATH;
      r.UPLOAD_DATE = json.UPLOAD_DATE;

      r.STAGE = Stage.parse(json.STAGE);
      r.STATE = State.parse(json.STATE);

      r.ASSIGNEE = User.parse(json.ASSIGNEE);

      r.HISTORY = History.parseArray(json.HISTORY);
      r.CHECK_LIST = CheckList.parse(json.CHECK_LIST);
      // r.WATCHERS = User.parseArray(json.WATCHERS);
      r.WATCHERS = User.parseArray(json.REPORT_WATCHERS);
      r.IS_WATCHING = Boolean(json.IS_WATCHING);

      r.DELAYS = ReportDelay.parseArray(json.DELAYS);

      if (json.ACTIVE_DELAY)
        r.ACTIVE_DELAY = ReportDelay.parse(json.ACTIVE_DELAY);

      if (json.LINKS)
        r.LINKS = Link.parseArray(json.LINKS);

      r.DOCUMENT_ID = json.DOCUMENT_ID;

      if (json.DOCUMENT_STATE)
        r.DOCUMENT_STATE = DocumentState[<string>json.DOCUMENT_STATE];

      if(json.DOCUMENTS)
        r.DOCUMENTS = Document.parseArray(json.DOCUMENTS);
      else
        r.DOCUMENTS = [];

      r.NOTES = json.NOTES;
      r.COMMENTS = json.COMMENTS;

      r.OVERRIDE = json.OVERRIDE;
      r.OVERRIDE_NAME = json.OVERRIDE_NAME;

      r.ADDENDUM = json.ADDENDUM;
      r.ADDENDUM_CONTENT = json.ADDENDUM_CONTENT;

      if(json.DATE_ANTICIPATED)
        r.DATE_ANTICIPATED = moment(json.DATE_ANTICIPATED).format('MMM DD, YYYY');

      r.REPORT_TYPE = ReportType[<string>json.REPORT_TYPE];

      r.INITIAL_SENT = json.INITIAL_SENT;

      if (json.REPORT_NOTES)
        r.REPORT_NOTES = ReportNote.parseArray(json.REPORT_NOTES);

      r.INTERNAL_FILE_NAME = json.INTERNAL_FILE_NAME;
      r.FILE_LOCATION = json.FILE_LOCATION;

      if(json.PARENT_ID)
        r.PARENT_ID = json.PARENT_ID;

      if(json.DUPLICATE_PARENT_ID)
        r.DUPLICATE_PARENT_ID = json.DUPLICATE_PARENT_ID;

      r.TYPE = SystemType[<string>json.TYPE];

      if (json.DATE_EFFECTIVE)
        r.DATE_EFFECTIVE = moment(json.DATE_EFFECTIVE).format('MMM DD, YYYY');

      r.STANDARD_OF_VALUES = json.STANDARD_OF_VALUES;

      if(json.REPORT_QUOTE)
        r.REPORT_QUOTE = new Quote(json.REPORT_QUOTE);

      r.ORDER = Boolean(json.ORDER);
      r.IS_QUOTE = Boolean(json.IS_QUOTE);

      if (json.FOLLOW_UPS)
        r.FOLLOW_UPS = ReportFollowUp.parseArray(json.FOLLOW_UPS);

      if (json.LINKED_REPORTS)
        r.LINKED_REPORTS = ReportLink.parseArray(json.LINKED_REPORTS);

      r.SYNCHRONIZE = json.SYNCHRONIZE;

      r.BVQ_ID = json.BVQ_ID;
      r.ORDER_ID = json.ORDER_ID;
      r.R_RESEARCH = User.parse(json.R_RESEARCH);
      r.R_SPREAD = User.parse(json.R_SPREAD);
      r.NOTES_COUNT = json.NOTES_COUNT;
      r.PROJECT_NUMBER = json.PROJECT_NUMBER;

      if (json.LINK_ACCESS)
        r.LINK_ACCESS = LinkAccess.parseArray(json.LINK_ACCESS);

      if(json.BVQ)
        r.BVQ = new BVQReport(json.BVQ);

      r.HAS_BVQ = json.HAS_BVQ;
      r.RATING = json.RATING;
      r.ENG_LETTER_CONTENT = json.ENG_LETTER_CONTENT;
      r.VALUE_ASSESSMENT = json.VALUE_ASSESSMENT;
      r.ENG_LETTER_CONTENT_DS = json.ENG_LETTER_CONTENT_DS;
      r.DAY_DIFF = json.DAY_DIFF;
      r.FIRST_QUOTE_FOLLOW_UP_TIME = json.FIRST_QUOTE_FOLLOW_UP_TIME;
      r.SECOND_QUOTE_FOLLOW_UP_TIME = json.SECOND_QUOTE_FOLLOW_UP_TIME;
      r.FIRST_QUOTE_FOLLOW_UP = json.FIRST_QUOTE_FOLLOW_UP;
      r.SECOND_QUOTE_FOLLOW_UP = json.SECOND_QUOTE_FOLLOW_UP;
      r.INDIVIDUAL_CAN_ANSWER = json.INDIVIDUAL_CAN_ANSWER;
      r.LL_NUMBER = json.LL_NUMBER;
      r.INDIVIDUAL_CAN_ANSWER = json.INDIVIDUAL_CAN_ANSWER;

      return r;
    }
  }

  static parseArray(json: any[]): Report[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(Report.parse(j));

    return arr;
  }

  getUsers(): ReportUsers {
    if (!this.USERS) this.USERS = new ReportUsers();
    return this.USERS;
  }

  getDelays(): ReportDelay[] {
    if (!this.DELAYS) this.DELAYS = [];
    return this.DELAYS;
  }

  get primaryDisplayName(): string {
    if (this.INTERNAL_FILE_NAME)
      return this.INTERNAL_FILE_NAME;

    if (this.OVERRIDE)
      return this.OVERRIDE_NAME;

    if (!this.ENTITIES)
      return 'No Primary Entity';

    for (const e of this.ENTITIES) {
      if (e.IS_DEFAULT)
        return e.NAME;
    }

    return 'No Primary Entity';
  }

  primaryEntity(user?: User): string {
    if(user && user.NAME_PREF) {
      switch (user.NAME_PREF) {
      case 'CORPORATE':
        for (const e of this.ENTITIES) {
          if (e.IS_DEFAULT && e.NAME)
            return e.NAME;
        }
        break;
      case 'BUSINESS':
        for (const e of this.ENTITIES) {
          if (e.IS_DEFAULT && e.DBA)
            return e.DBA;
        }
        break;
      case 'INTERNAL':
        if(this.INTERNAL_FILE_NAME)
          return this.INTERNAL_FILE_NAME;
        break;
      default:
        break;
      }
    }

    if (this.OVERRIDE && this.OVERRIDE_NAME)
      return this.OVERRIDE_NAME;

    if (this.ENTITIES){
      for (const e of this.ENTITIES) {
        if (e.IS_DEFAULT) {
          if(e.NAME)
            return e.NAME;
          if(e.DBA)
            return e.DBA;
        }
      }
    }

    if(this.INTERNAL_FILE_NAME)
      return this.INTERNAL_FILE_NAME;

    return 'No Primary Entity';
  }

  getEntitiesName(): string {
    if (this.OVERRIDE && this.OVERRIDE_NAME)
      return this.OVERRIDE_NAME;

    if (this.ENTITIES){
      let names = [];

      for (const e of this.ENTITIES) {
        if (e.IS_DEFAULT)
          names.push(e.NAME);
      }

      for (const e of this.ENTITIES) {
        if (!e.IS_DEFAULT)
          names.push(e.NAME);
      }

      return names.join(", ");
    }

    return 'No Primary Entity';
  }

  getNonPrimaryEntities(separator: string): string {
    const eArr: string[] = [];
    if (this.ENTITIES != null) {
      for (const e of this.ENTITIES) {
        if (!e.IS_DEFAULT) {
          eArr.push(e.NAME);
        }

      }
    }

    if (eArr.length > 0) {
      const lastIndex: number = eArr.length - 1;
      const lastEntity: string = eArr[lastIndex];
      eArr.splice(lastIndex - 1, 1);

      if (eArr.length > 0)
        return `, ${eArr.join(separator)} and ${lastEntity}`;
      else
        return ` and ${lastEntity}`;

    }

    return null;
  }

  get primaryEntityDba(): string {
    if (!this.ENTITIES)
      return  null;

    for (const e of this.ENTITIES) {
      if (e.IS_DEFAULT)
        return e.DBA;
    }

    return null;
  }

  hasPrimaryEntity(): boolean {
    if (this.ENTITIES) {
      return this.ENTITIES.length > 0;
    } else return false;
  }

  get validateSigners(): string[] {
    const errors = [];

    if (!this.CLIENT) {
      errors.push('Bank Not Chosen');
      return errors;
    }

    if (!this.CLIENT_USERS) {
      errors.push('Client Users Not Chosen');
      return errors;
    }

    if (this.CLIENT_USERS.length === 0) {
      errors.push('Client Users Not Chosen');
      return errors;
    }

    let hasOneUser = false;
    let engagedClientUser = null;
    for (const usr of this.CLIENT_USERS) {
      if (usr.EMAIL_ENGAGE === true) {
        hasOneUser = true;
        engagedClientUser = ClientUser.parse(usr.CLIENT_USER);
        break;
      }
    }

    if (engagedClientUser != null) {

      if (!engagedClientUser.NAME) {
        errors.push('Client User has no Name');
        return errors;
      }

      if (!engagedClientUser.EMAIL) {
        errors.push('Client User has no Email Address');
        return errors;
      }

      if (!engagedClientUser.getFullAddress()) {
        errors.push('Client User has no Address');
        return errors;
      }

      if (!engagedClientUser.ADDRESS) {
        errors.push('Client User has no Address');
        return errors;
      }

      if (!engagedClientUser.CITY) {
        errors.push('Client User has no City');
        return errors;
      }

      if (!engagedClientUser.STATE) {
        errors.push('Client User has no State');
        return errors;
      }

      if (!engagedClientUser.ZIP) {
        errors.push('Client User has no Zip');
        return errors;
      }
    }

    if (!hasOneUser)
      errors.push('No Client User to Sign Letter');

    return errors;
  }

  get validateDataEntry(): string[] {
    const errors = [];

    if (!this.VALUATION_TYPE)
      errors.push('Type of Valuation is Not Chosen');

    if (!this.CHARGE)
      errors.push('Fee is Not Chosen');

    if (!this.TURN_FROM)
      errors.push('Turn Time From is Not Chosen');

    if (!this.TURN_TO)
      errors.push('Turn Time To is Not Chosen');

    return errors;
  }

  get validateEffectiveDate(): string[] {
    const errors = [];

    if (!this.DATE_EFFECTIVE)
      errors.push('No Effective Date');

    return errors;
  }

  get validateAll(): string[] {
    const vSigners = this.validateSigners;
    const vData = this.validateDataEntry;

    return vSigners.concat(vData);
  }

  get isValid(): boolean {
    return this.validateAll.length === 0;
  }

  get emptyProcessor(): boolean {
    return this.USERS.PROCESSOR == null;
  }


  /**
   * Show Engage File Button
   * true - if no DOCUMENT_ID and no DOCUMENTS
   * true - if DOCUMENT_STATE == EXECUTED for DocuSign or Manually Engaged
   */
  showEngage(): boolean {
    if(!this.DOCUMENT_ID && this.DOCUMENTS.length ===0)
      return true;

    if(this.DOCUMENTS.length > 0)
      for(let doc of this.DOCUMENTS)
        if(doc.DOCUMENT_STATE === DocumentState.executed || doc.IS_MANUAL)
          return true;

    return false;
  }

  showResendEnggletter(isManual: boolean = false): boolean {
    if(!this.DOCUMENT_ID && this.DOCUMENTS.length ===0)
      return false;

    if(this.DOCUMENTS.length > 0)
      for(let doc of this.DOCUMENTS)
         if(doc.DOCUMENT_STATE === DocumentState.voided || doc.IS_MANUAL)
          return true;

    return false;
  }

  hasClientCollector(): boolean {
    if (this.CLIENT) {
      if (this.CLIENT.COLLECTOR) {
        return true;
      }
    }
    return false;
  }

  getDueDate(): string {
    if (this.DATE_ANTICIPATED != null)
      return this.DATE_ANTICIPATED;
    else
      return this.DATE_DUE;
  }

  get turnDiff(): number {
    return Number(this.TURN_TO) - Number(this.TURN_FROM);
  }

  getName(): string {
    return this.primaryEntity();
  }

  getFullDetails(): string {
    return this.TYPE + ' - ' + this.primaryEntity();
  }

  getType(): SearchableSelectionType {
    return SearchableSelectionType.VALUE;
  }

  getValue(): any {
    return this;
  }

  getReportType(): string {
    let reportType = 'R';

    if(this.IS_QUOTE)
      reportType = 'Q';
    else if(this.ORDER_ID)
      reportType = 'O';

    return `(${reportType}) ${this.TYPE} - ${this.primaryEntity()}`;
  }


  get nextFollowUpTime(): number {
    if(this.IS_FIRST_FOLLOWUP_COMPLETED){
      if(this.SECOND_QUOTE_FOLLOW_UP == null)
        return 0;
      else return this.SECOND_QUOTE_FOLLOW_UP;
    }
    else {
      if(this.FIRST_QUOTE_FOLLOW_UP == null)
        return 0;
      else
        return this.FIRST_QUOTE_FOLLOW_UP;
    }
  }

  getClientUserByType(type: ReportClientType): ReportClientUser {
    if (this.CLIENT_USERS) {
      for (const cl of this.CLIENT_USERS)
        if (cl.TYPE === type) {
          return cl;
        }

    }
    return null;
  }

}


export class ReportClientUser implements SearchableItem {
  ID: string;
  CLIENT_USER: ClientUser;
  TYPE: ReportClientType;
  LABEL: string;
  EMAIL_CC: boolean;
  EMAIL_ENGAGE: boolean;
  MAIN_POINT_CONTACT: boolean;
  SEC_CONTACT: boolean;

  active: boolean;
  allowed = false;

  static parse(json: any): ReportClientUser {
    if (json) {
      const c = new ReportClientUser();

      c.ID = json.ID;
      c.CLIENT_USER = json.CLIENT_USER ? ClientUser.parse(json.CLIENT_USER) : null;
      c.TYPE = ReportClientType[<string>json.TYPE];
      c.LABEL = json.LABEL;
      c.EMAIL_CC = <boolean>json.EMAIL_CC;
      c.EMAIL_ENGAGE = <boolean>json.EMAIL_ENGAGE;
      c.MAIN_POINT_CONTACT = <boolean>json.MAIN_POINT_CONTACT;
      c.SEC_CONTACT = <boolean>json.SEC_CONTACT;

      return c;
    } else return null;
  }

  static parseArray(json: any[]): ReportClientUser[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(ReportClientUser.parse(j));

    return arr;
  }

  getName(): string {
    return this.CLIENT_USER ? this.CLIENT_USER.getName() : null;
  }

  getFullDetails(): string {
    return this.CLIENT_USER ? this.CLIENT_USER.getName() + ` <${this.CLIENT_USER.EMAIL}>` : null;
  }

  getType(): SearchableSelectionType {
    return SearchableSelectionType.VALUE;
  }

  getValue(): any {
    return this;
  }
}

export class ReportUsers {
  PROCESSOR: ReportUser;
  RESEARCH_ANALYST: ReportUser;
  FINANCIAL_ANALYST: ReportUser;
  SENIOR_ANALYST: ReportUser;

  //Report Ea
  EQUIPMENT_PROCESSOR: ReportUser;
  EQUIPMENT_RESEARCH_ANALYST: ReportUser;
  EQUIPMENT_APPRAISER: ReportUser;
  EQUIPMENT_REVIEWER: ReportUser;
  EQUIPMENT_SUBMITTER: ReportUser;

  OTHER: ReportUser;


  EA_PROCESSOR: ReportUser;
  EA_RESEARCH_ANALYST: ReportUser;

  static parse(json: any): ReportUsers {
    if (json) {
      const r = new ReportUsers();

      if(json.PROCESSOR)
        r.PROCESSOR = ReportUser.parse(json.PROCESSOR);

      if (json.RESEARCH_ANALYST)
        r.RESEARCH_ANALYST = ReportUser.parse(json.RESEARCH_ANALYST);

      if (json.FINANCIAL_ANALYST)
        r.FINANCIAL_ANALYST = ReportUser.parse(json.FINANCIAL_ANALYST);

      if (json.SENIOR_ANALYST)
        r.SENIOR_ANALYST = ReportUser.parse(json.SENIOR_ANALYST);

      if (json.EQUIPMENT_PROCESSOR)
        r.EQUIPMENT_PROCESSOR = ReportUser.parse(json.EQUIPMENT_PROCESSOR);

      if (json.EQUIPMENT_RESEARCH_ANALYST)
        r.EQUIPMENT_RESEARCH_ANALYST = ReportUser.parse(json.EQUIPMENT_RESEARCH_ANALYST);

      if (json.EQUIPMENT_APPRAISER)
        r.EQUIPMENT_APPRAISER = ReportUser.parse(json.EQUIPMENT_APPRAISER);

      if (json.EQUIPMENT_REVIEWER)
        r.EQUIPMENT_REVIEWER = ReportUser.parse(json.EQUIPMENT_REVIEWER);

      if (json.EQUIPMENT_SUBMITTER)
        r.EQUIPMENT_SUBMITTER = ReportUser.parse(json.EQUIPMENT_SUBMITTER);

      r.OTHER = ReportUser.parse(json.OTHER);

      if(json.EA_PROCESSOR)
        r.EA_PROCESSOR = ReportUser.parse(json.EA_PROCESSOR);

      return r;
    } else return null;
  }
}

export class ReportUser {
  USER: User;
  LABEL: string;

  static parse(json: any): ReportUser {
    if (json) {
      const r = new ReportUser();

      r.USER = User.parse(json.USER);
      r.LABEL = json.LABEL;

      return r;
    } else return null;
  }

  static parseArray(json: any[]): ReportUser[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(ReportUser.parse(j));

    return arr;
  }
}

export class Link {
  ID: string;
  HASH: string;
  TYPE: LinkType;
  DATE_CREATED: Date;
  DATE_EXPIRES: Date;
  MODULE_TYPE: ModuleType;
  DISABLE_FOLLOWUP: boolean;
  EMAIL: string;

  selected = false;
  copied: boolean = false;

  static parse(json: any): Link {
    if (json) {
      const l = new Link();

      l.ID = json.ID;
      l.HASH = json.HASH;
      l.TYPE = LinkType[<string>json.TYPE];
      l.DATE_CREATED = json.DATE_CREATED;
      l.DATE_EXPIRES = json.DATE_EXPIRES;
      l.MODULE_TYPE = ModuleType[<string>json.MODULE_TYPE];
      l.DISABLE_FOLLOWUP = json.DISABLE_FOLLOWUP;
      l.EMAIL = json.EMAIL;

      return l;
    } else return null;
  }

  static parseArray(json: any[]): Link[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(Link.parse(j));

    return arr;
  }
}

export class ReportDelay {
  ID: string;
  DATE_START: Date;
  DATE_END: Date;
  REASON_START: string;
  REASON_END: string;
  USER_START: User;
  USER_END: User;
  NOTIFY_CLIENT: boolean;
  DELAY_TYPE: DelayType;
  NOTE_START: string;
  NOTE_END: string;

  static parse(json: any): ReportDelay {
    if (json) {
      const d = new ReportDelay();

      d.ID = json.ID;
      if (json.DATE_START)
        d.DATE_START = new Date(json.DATE_START);
      if (json.DATE_END)
        d.DATE_END = new Date(json.DATE_END);
      d.REASON_START = json.REASON_START;
      d.REASON_END = json.REASON_END;

      d.USER_START = User.parse(json.USER_START);
      d.USER_END = User.parse(json.USER_END);
      d.NOTIFY_CLIENT = json.NOTIFY_CLIENT;
      d.DELAY_TYPE = DelayType[<string>json.DELAY_TYPE];

      d.NOTE_START = json.NOTE_START;
      d.NOTE_END = json.NOTE_END;

      return d;
    } else return null;
  }

  static parseArray(json: any[]): ReportDelay[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(ReportDelay.parse(j));

    return arr;
  }
}

export class ReportDelayResult {
  DELAY: ReportDelay;
  STATE: State;

  static parse(json: any): ReportDelayResult {
    if (json) {
      const d = new ReportDelayResult();

      d.DELAY = ReportDelay.parse(json.DELAY);
      d.STATE = State.parse(json.STATE);

      return d;
    } else return null;
  }
}

export class ReportEngageResult {
  HISTORY_STAGE: HistoryStage;
  DOCUMENT_ID: string;
  DOCUMENT_STATE: DocumentState;
  DATE_ENGAGED_LETTER: string;
  DATE_ENGAGED: string;
  DATE_DUE: string;

  static parse(json: any): ReportEngageResult {
    if (json) {
      const e = new ReportEngageResult();

      e.HISTORY_STAGE = HistoryStage.parse(json.HISTORY_STAGE);
      e.DOCUMENT_ID = json.DOCUMENT_ID;
      if (json.DOCUMENT_STATE)
        e.DOCUMENT_STATE = DocumentState[<string>json.DOCUMENT_STATE];
      if (json.DATE_ENGAGED_LETTER)
        e.DATE_ENGAGED_LETTER = json.DATE_ENGAGED_LETTER;
      if (json.DATE_ENGAGED)
        e.DATE_ENGAGED = json.DATE_ENGAGED;
      if (json.DATE_DUE)
        e.DATE_DUE = json.DATE_DUE;

      return e;
    } else return null;
  }
}

export class ReportSiteVisitResult {
  HISTORY_STAGE: HistoryStage;
  DATE_SITE_VISIT_EMAIL: string;

  static parse(json: any): ReportSiteVisitResult {
    if (json) {
      const e = new ReportSiteVisitResult();

      e.HISTORY_STAGE = HistoryStage.parse(json.HISTORY_STAGE);
      if (json.DATE_SITE_VISIT_EMAIL)
        e.DATE_SITE_VISIT_EMAIL = json.DATE_SITE_VISIT_EMAIL;

      return e;
    } else return null;
  }
}

export class ReportRecalculatedResult {
  DATE_ENGAGED: string;
  DATE_DUE: string;

  static parse(json: any): ReportRecalculatedResult {
    if (json) {
      const e = new ReportRecalculatedResult();

      if (json.DATE_ENGAGED)
        e.DATE_ENGAGED = moment(json.DATE_ENGAGED).format('MMMM DD, YYYY');

      if (json.DATE_DUE)
        e.DATE_DUE = moment(json.DATE_DUE).format('MMMM DD, YYYY');

      return e;
    } else return null;
  }
}

export class RecentReport {
  ID: string;
  COMPANY_NAME: string;

  constructor(report: Report) {
    if (report !== null) {
      this.ID = report.ID;
      this.COMPANY_NAME = report.primaryEntity();
    }
  }
}

export class StageChangeRequest {
  ID: string;
  ASSIGNEE_TYPE: AssigneeType;
  ASSIGNEE_TYPE_USER: ReportUserType;
  ASSIGNEE_ID: string;
  NOTES: string;
  NOTIFY_CLIENT: boolean;
  FILE_LOCATION: string;
  RATING: number;
  DELAY: DelayType;
  ERROR : ErrorType;
  VALUE_ASSESSMENT: string;
  // Helper
  reportUserType: ReportUserType;
}

export class ReportTierError {
  COMPANY_NAME: string;
  ERROR: string;
  ASSIGNEE: string;
  RATING: number;
  DATE_SUBMITTED: string;

  constructor(value: any = {}) {
    Object.assign(this, value);
  }

  static parseArray(json: any[]): ReportTierError[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(new ReportTierError(j));

    return arr;
  }
}

export class DropOffReport {
  BANK_NAME: string;
  PERIOD_LAST_YEAR: number;
  PERIOD_THIS_YEAR: number;
  CHANGE: string;

  constructor(value: any = {}) {
    Object.assign(this, value);
  }

  static parseArray(json: any[]): DropOffReport[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(new DropOffReport(j));

    return arr;
  }
}

export class IndustryUsageReport {
  INDUSTRY: string;
  REPORT_COUNT: number;

  constructor(value: any = {}) {
    Object.assign(this, value);
  }

  static parseArray(json: any[]): IndustryUsageReport[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(new IndustryUsageReport(j));

    return arr;
  }
}

export enum ReportClientType {
  PROCESSOR = 'PROCESSOR',
  BDO = 'BDO',
  UNDERWRITER = 'UNDERWRITER',
  LSP = 'LSP',
  OTHER = 'OTHER'
}

export enum ReportUserType {
  PROCESSOR = 'PROCESSOR',
  RESEARCH_ANALYST = 'RESEARCH_ANALYST',
  FINANCIAL_ANALYST = 'FINANCIAL_ANALYST',
  SENIOR_ANALYST = 'SENIOR_ANALYST',
  SENIOR_APPRAISER = 'SENIOR_APPRAISER',
  SENIOR_REVIEWER = 'SENIOR_REVIEWER',

  // Report Ea
  EQUIPMENT_PROCESSOR = 'EQUIPMENT_PROCESSOR',
  EQUIPMENT_RESEARCH_ANALYST = 'EQUIPMENT_RESEARCH_ANALYST',
  EQUIPMENT_APPRAISER = 'EQUIPMENT_APPRAISER',
  EQUIPMENT_REVIEWER = 'EQUIPMENT_REVIEWER',
  EQUIPMENT_SUBMITTER = 'EQUIPMENT_SUBMITTER',

  OTHER = 'OTHER'
}

export enum StandardOfValue {
  FAIR_MARKET_VALUE = 'FAIR_MARKET_VALUE',
  INVESTMENT_VALUE = 'INVESTMENT_VALUE',

  //Report Ea
  ORDERLY_LIQUIDATION_VALUE = 'ORDERLY_LIQUIDATION_VALUE',
  FORCED_LIQUIDATION_VALUE = 'FORCED_LIQUIDATION_VALUE',
  FAIR_MARKET_VALUE_INSTALLED = 'FAIR_MARKET_VALUE_INSTALLED',
  LIQUIDATION_VALUE_IN_PLACE = 'LIQUIDATION_VALUE_IN_PLACE',
  SALVAGE_VALUE = 'SALVAGE_VALUE',
  SCRAP_VALUE = 'SCRAP_VALUE',
  SEE_NOTE = 'SEE_NOT',
  OTHER = 'OTHER',
}

export enum ReportValuationType {
  STOCK = 'STOCK',
  ASSET = 'ASSET',
  TO_BE_DETERMINED = 'TO_BE_DETERMINED',

  //Report Ea
  DESKTOP = 'DESKTOP',
  SUMMARY = 'SUMMARY',
  BOTH = 'BOTH'
}

export enum AssigneeType {
  USER = 'USER',
  ROLE = 'ROLE'
}

export enum LinkType {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
  ALL = 'ALL',
  ASK_USER = 'ASK_USER'
}

export enum LinkExpireState {
  ALL = 'ALL',
  EXPIRED = 'EXPIRED',
  LIVE = 'LIVE'
}

export enum DocumentState {
  draft = 'draft',
  pending = 'pending',
  executed = 'executed',
  voided = 'voided',
  expired = 'expired'
}

export enum ReportType {
  CONCLUSION = 'CONCLUSION',
  CALCULATION = 'CALCULATION',

  // Report Ea
  NO_SITE_VISIT = 'NO_SITE_VISIT',
  WITH_SITE_VISIT = 'WITH_SITE_VISIT',
  LIQUOR_LICENSE = 'LIQUOR_LICENSE',
  INVENTORY_APPRAISAL = 'INVENTORY_APPRAISAL'
}

export enum DelayType {
  CLIENT = 'CLIENT',
  RELIANT = 'RELIANT',
  CURRENT = 'CURRENT'
}

export enum ErrorType {
  NO_ERRORS ="NO_ERRORS",
  MINOR_ERRORS ="MINOR_ERRORS",
  MAJOR_ERRORS="MAJOR_ERRORS"
}
